import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  Check,
  Users,
  BarChart,
  Goal,
  ChevronsRight,
  BookOpen,
  Clock,
  Target,
  Star,
} from "lucide-react";
import { Helmet } from "react-helmet";

const HowToPlayPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          How to Play Bingo - Rules, Tips, and Variations | Call My Bingo
        </title>
        <meta
          name="description"
          content="Learn how to play bingo with our comprehensive guide. Discover rules for different bingo variations including 75-ball, 90-ball, and speed bingo. Get expert tips and strategies."
        />
        <meta
          name="keywords"
          content="how to play bingo, bingo rules, bingo instructions, bingo game guide, bingo variations, 75-ball bingo, 90-ball bingo"
        />
        <link
          rel="canonical"
          href="https://callmybingo.com/how-to-play-bingo"
        />
        <meta
          property="og:title"
          content="How to Play Bingo - Complete Guide to Bingo Rules and Variations"
        />
        <meta
          property="og:description"
          content="Learn how to play different types of bingo games with our easy-to-follow guide. Perfect for beginners and experienced players alike."
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content="https://callmybingo.com/how-to-play-bingo"
        />
        <meta
          property="og:image"
          content="https://callmybingo.com/assets/bingo-how-to-play.jpg"
        />
      </Helmet>

      <div className="bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              How to Play Bingo
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto">
              Your complete guide to all bingo variations, rules, and winning
              strategies
            </p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            {/* Breadcrumbs */}
            <div className="bg-gray-50 py-3 px-6 text-sm text-gray-500">
              <Link to="/" className="hover:text-purple-600 transition-colors">
                Home
              </Link>
              <span className="mx-2">&gt;</span>
              <span className="text-purple-800 font-medium">
                How to Play Bingo
              </span>
            </div>

            {/* Article Content */}
            <div className="p-6 md:p-8 lg:p-10">
              {/* Introduction */}
              <section className="mb-12 max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-800 mb-6">
                  Introduction to Bingo
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Bingo is a classic game of chance that's enjoyed by millions
                  around the world. Simple to learn but always exciting, bingo
                  brings people together in a fun atmosphere of anticipation and
                  friendly competition. Whether you're playing in a community
                  hall, casino, or online, the thrill of watching your card fill
                  up and shouting "BINGO!" remains the same.
                </p>

                <div className="flex items-center p-4 bg-purple-50 border-l-4 border-purple-500 rounded-r-md">
                  <BookOpen
                    className="text-purple-600 mr-4 flex-shrink-0"
                    size={24}
                  />
                  <p className="text-purple-800">
                    This guide covers everything from the basic rules to
                    advanced strategies, making it perfect for new players and
                    those looking to refine their bingo knowledge.
                  </p>
                </div>
              </section>

              {/* Jump Links */}
              <section className="mb-12 max-w-4xl mx-auto">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    Quick Navigation
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    <a
                      href="#basics"
                      className="flex items-center p-3 bg-white rounded-md border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
                    >
                      <span className="bg-purple-100 p-2 rounded-full mr-3">
                        <Award size={18} className="text-purple-600" />
                      </span>
                      <span className="text-gray-700 font-medium">
                        The Basics
                      </span>
                    </a>
                    <a
                      href="#variations"
                      className="flex items-center p-3 bg-white rounded-md border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
                    >
                      <span className="bg-purple-100 p-2 rounded-full mr-3">
                        <BarChart size={18} className="text-purple-600" />
                      </span>
                      <span className="text-gray-700 font-medium">
                        Game Variations
                      </span>
                    </a>
                    <a
                      href="#patterns"
                      className="flex items-center p-3 bg-white rounded-md border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
                    >
                      <span className="bg-purple-100 p-2 rounded-full mr-3">
                        <Target size={18} className="text-purple-600" />
                      </span>
                      <span className="text-gray-700 font-medium">
                        Winning Patterns
                      </span>
                    </a>
                    <a
                      href="#tips"
                      className="flex items-center p-3 bg-white rounded-md border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
                    >
                      <span className="bg-purple-100 p-2 rounded-full mr-3">
                        <Goal size={18} className="text-purple-600" />
                      </span>
                      <span className="text-gray-700 font-medium">
                        Tips & Strategies
                      </span>
                    </a>
                  </div>
                </div>
              </section>

              {/* The Basics */}
              <section
                id="basics"
                className="mb-16 max-w-4xl mx-auto scroll-mt-20"
              >
                <div className="flex items-center mb-6">
                  <Award className="text-purple-600 mr-3" size={28} />
                  <h2 className="text-3xl font-bold text-gray-800">
                    The Basics of Bingo
                  </h2>
                </div>

                <div className="space-y-6 text-gray-600">
                  <h3 className="text-2xl font-semibold text-gray-700">
                    How Bingo Works
                  </h3>

                  <p>
                    Bingo is played with cards featuring a grid of numbers. A
                    caller (or automated system) randomly selects numbers, and
                    players mark these numbers on their cards. The goal is to
                    complete a specific pattern (like a row, column, or
                    diagonal) before other players.
                  </p>

                  <div className="grid md:grid-cols-2 gap-6 my-8">
                    <div className="bg-gray-50 p-5 rounded-lg">
                      <h4 className="font-bold text-purple-700 mb-3">
                        The Equipment
                      </h4>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <Check
                            size={18}
                            className="text-green-500 mr-2 mt-1 flex-shrink-0"
                          />
                          <span>
                            <strong>Bingo cards</strong> - Pre-printed cards
                            with a grid of numbers
                          </span>
                        </li>
                        <li className="flex items-start">
                          <Check
                            size={18}
                            className="text-green-500 mr-2 mt-1 flex-shrink-0"
                          />
                          <span>
                            <strong>Markers or daubers</strong> - Used to mark
                            called numbers on cards
                          </span>
                        </li>
                        <li className="flex items-start">
                          <Check
                            size={18}
                            className="text-green-500 mr-2 mt-1 flex-shrink-0"
                          />
                          <span>
                            <strong>Number selection device</strong> - Such as a
                            ball cage, electronic random number generator, or
                            our online bingo caller
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-5 rounded-lg">
                      <h4 className="font-bold text-purple-700 mb-3">
                        Basic Steps
                      </h4>
                      <ol className="space-y-3 list-decimal pl-5">
                        <li>Each player receives at least one bingo card</li>
                        <li>The caller announces a randomly selected number</li>
                        <li>
                          Players check their cards and mark the number if
                          present
                        </li>
                        <li>
                          This continues until someone completes the required
                          pattern
                        </li>
                        <li>The winning player calls out "BINGO!"</li>
                        <li>The card is verified for a valid win</li>
                      </ol>
                    </div>
                  </div>

                  <h3 className="text-2xl font-semibold text-gray-700 pt-4">
                    Setting Up a Game
                  </h3>

                  <p>
                    Whether you're hosting a game at home, playing online, or at
                    a bingo hall, the setup follows similar principles. You'll
                    need to decide on:
                  </p>

                  <ul className="space-y-3 pl-5 list-disc">
                    <li>
                      <strong>Game type</strong> - Which variation of bingo to
                      play (75-ball, 90-ball, etc.)
                    </li>
                    <li>
                      <strong>Winning patterns</strong> - What arrangements
                      players need to complete
                    </li>
                    <li>
                      <strong>Prizes</strong> - What winners will receive
                    </li>
                    <li>
                      <strong>Number of games</strong> - How many rounds you'll
                      play
                    </li>
                  </ul>

                  <div className="p-4 bg-purple-50 rounded-lg mt-4">
                    <div className="flex items-start">
                      <Clock
                        size={24}
                        className="text-purple-600 mr-3 flex-shrink-0"
                      />
                      <p className="text-purple-800 text-sm">
                        <strong>Pro Tip:</strong> For beginners, start with a
                        simple straight-line pattern and slower calling pace. As
                        players become familiar with the game, you can introduce
                        more complex patterns and increase the speed.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Variations */}
              <section
                id="variations"
                className="mb-16 max-w-4xl mx-auto scroll-mt-20"
              >
                <div className="flex items-center mb-6">
                  <BarChart className="text-purple-600 mr-3" size={28} />
                  <h2 className="text-3xl font-bold text-gray-800">
                    Bingo Variations
                  </h2>
                </div>

                <div className="space-y-6 text-gray-600">
                  <p>
                    Bingo has evolved into several popular variations around the
                    world, each with its own card design, rules, and winning
                    patterns.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* 75-Ball Bingo */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-indigo-100 py-4 px-5">
                        <h3 className="text-xl font-bold text-indigo-800">
                          75-Ball Bingo
                        </h3>
                        <p className="text-sm text-gray-700">
                          American style bingo
                        </p>
                      </div>
                      <div className="p-5 space-y-4">
                        <p>
                          The classic American version played on a 5×5 grid with
                          a free space in the center.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-indigo-600 mr-2"
                            />
                            <span>5×5 grid with 24 numbers</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-indigo-600 mr-2"
                            />
                            <span>Center square is a "free" space</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-indigo-600 mr-2"
                            />
                            <span>Columns labeled B-I-N-G-O</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-indigo-600 mr-2"
                            />
                            <span>Common patterns: lines, letters, shapes</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* 90-Ball Bingo */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-pink-100 py-4 px-5">
                        <h3 className="text-xl font-bold text-pink-800">
                          90-Ball Bingo
                        </h3>
                        <p className="text-sm text-gray-700">
                          British style bingo
                        </p>
                      </div>
                      <div className="p-5 space-y-4">
                        <p>
                          The traditional UK version played on cards with 3 rows
                          and 9 columns.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-pink-600 mr-2"
                            />
                            <span>3×9 grid with 15 numbers</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-pink-600 mr-2"
                            />
                            <span>Each row contains 5 numbers</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-pink-600 mr-2"
                            />
                            <span>
                              Three winning stages: 1 line, 2 lines, full house
                            </span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-pink-600 mr-2"
                            />
                            <span>
                              Popular in UK, Australia, and parts of Europe
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* 30-Ball Bingo */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-yellow-100 py-4 px-5">
                        <h3 className="text-xl font-bold text-yellow-800">
                          30-Ball Bingo
                        </h3>
                        <p className="text-sm text-gray-700">Speed bingo</p>
                      </div>
                      <div className="p-5 space-y-4">
                        <p>
                          A fast-paced version for players looking for quick,
                          exciting games.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-yellow-600 mr-2"
                            />
                            <span>3×3 grid with 9 numbers</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-yellow-600 mr-2"
                            />
                            <span>Typically only one pattern: full card</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-yellow-600 mr-2"
                            />
                            <span>Games typically last 2-3 minutes</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-yellow-600 mr-2"
                            />
                            <span>
                              Perfect for quick rounds and energetic play
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* 80-Ball Bingo */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-green-100 py-4 px-5">
                        <h3 className="text-xl font-bold text-green-800">
                          80-Ball Bingo
                        </h3>
                        <p className="text-sm text-gray-700">Online favorite</p>
                      </div>
                      <div className="p-5 space-y-4">
                        <p>
                          Primarily found in online bingo rooms, offering a
                          middle ground between other variations.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-green-600 mr-2"
                            />
                            <span>4×4 grid with 16 numbers</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-green-600 mr-2"
                            />
                            <span>Each column has a different color</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-green-600 mr-2"
                            />
                            <span>Various winning patterns</span>
                          </li>
                          <li className="flex items-center">
                            <ChevronsRight
                              size={16}
                              className="text-green-600 mr-2"
                            />
                            <span>Popular in online bingo platforms</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-6 rounded-lg">
                    <h4 className="text-xl font-semibold text-purple-800 mb-3">
                      Which Bingo Variation Is Right For You?
                    </h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <Users
                          size={18}
                          className="text-purple-600 mr-2 mt-1 flex-shrink-0"
                        />
                        <span>
                          <strong>For large groups:</strong> 75-ball bingo
                          offers more winning opportunities with its variety of
                          patterns
                        </span>
                      </li>
                      <li className="flex items-start">
                        <Users
                          size={18}
                          className="text-purple-600 mr-2 mt-1 flex-shrink-0"
                        />
                        <span>
                          <strong>For traditional play:</strong> 90-ball bingo
                          provides the classic experience with multiple winning
                          stages
                        </span>
                      </li>
                      <li className="flex items-start">
                        <Users
                          size={18}
                          className="text-purple-600 mr-2 mt-1 flex-shrink-0"
                        />
                        <span>
                          <strong>For quick games:</strong> 30-ball bingo is
                          perfect when time is limited or for maintaining high
                          energy
                        </span>
                      </li>
                      <li className="flex items-start">
                        <Users
                          size={18}
                          className="text-purple-600 mr-2 mt-1 flex-shrink-0"
                        />
                        <span>
                          <strong>For online play:</strong> 80-ball bingo offers
                          a good balance of speed and complexity
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Winning Patterns */}
              <section
                id="patterns"
                className="mb-16 max-w-4xl mx-auto scroll-mt-20"
              >
                <div className="flex items-center mb-6">
                  <Target className="text-purple-600 mr-3" size={28} />
                  <h2 className="text-3xl font-bold text-gray-800">
                    Winning Patterns
                  </h2>
                </div>

                <div className="space-y-6 text-gray-600">
                  <p>
                    One of the most exciting aspects of bingo is the variety of
                    patterns that can be used as winning conditions. These range
                    from simple lines to complex shapes that add an extra
                    challenge and excitement to the game.
                  </p>

                  <h3 className="text-2xl font-semibold text-gray-700">
                    Common Patterns
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 my-6">
                    <div className="border border-gray-200 rounded-lg p-4 text-center hover:bg-purple-50 transition-colors">
                      <div className="text-3xl mb-2">🎯</div>
                      <h4 className="font-semibold text-purple-800">
                        Single Line
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Horizontal, vertical, or diagonal
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 text-center hover:bg-purple-50 transition-colors">
                      <div className="text-3xl mb-2">❌</div>
                      <h4 className="font-semibold text-purple-800">
                        Letter X
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Both diagonals marked
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 text-center hover:bg-purple-50 transition-colors">
                      <div className="text-3xl mb-2">🅱️</div>
                      <h4 className="font-semibold text-purple-800">
                        Letter B
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Left edge and curved right side
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 text-center hover:bg-purple-50 transition-colors">
                      <div className="text-3xl mb-2">🖼️</div>
                      <h4 className="font-semibold text-purple-800">
                        Picture Frame
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Outside border of the card
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 text-center hover:bg-purple-50 transition-colors">
                      <div className="text-3xl mb-2">◢◣◥◤</div>
                      <h4 className="font-semibold text-purple-800">
                        Four Corners
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        All four corners marked
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 text-center hover:bg-purple-50 transition-colors">
                      <div className="text-3xl mb-2">➕</div>
                      <h4 className="font-semibold text-purple-800">
                        Plus Sign
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Middle row and column
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 text-center hover:bg-purple-50 transition-colors">
                      <div className="text-3xl mb-2">♦️</div>
                      <h4 className="font-semibold text-purple-800">Diamond</h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Diamond shape from center
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 text-center hover:bg-purple-50 transition-colors">
                      <div className="text-3xl mb-2">⬛</div>
                      <h4 className="font-semibold text-purple-800">
                        Blackout
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        All numbers on the card
                      </p>
                    </div>
                  </div>

                  <div className="bg-indigo-50 p-5 rounded-lg border-l-4 border-indigo-500 mt-4">
                    <p className="text-indigo-800">
                      <strong>Customize Your Game:</strong> When hosting your
                      own bingo game, you can create custom patterns that match
                      your theme. For holiday parties, consider seasonal shapes
                      like trees, bells, or snowflakes!
                    </p>
                  </div>
                </div>
              </section>

              {/* Tips and Strategies Section */}
              <section
                id="tips"
                className="mb-16 max-w-4xl mx-auto scroll-mt-20"
              >
                <div className="flex items-center mb-6">
                  <Goal className="text-purple-600 mr-3" size={28} />
                  <h2 className="text-3xl font-bold text-gray-800">
                    Tips & Strategies
                  </h2>
                </div>

                <div className="space-y-6 text-gray-600">
                  <p>
                    While bingo is primarily a game of chance, there are
                    strategies and approaches that can enhance your experience
                    and potentially improve your odds of winning.
                  </p>

                  <div className="grid md:grid-cols-2 gap-6 my-6">
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-lg">
                      <h3 className="text-xl font-bold text-purple-800 mb-3 flex items-center">
                        <Star className="text-purple-500 mr-2" size={20} />
                        For Players
                      </h3>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <div className="bg-purple-100 rounded-full p-1 text-purple-600 mt-0.5 mr-2 flex-shrink-0">
                            <Check size={14} />
                          </div>
                          <span>
                            <strong>Play multiple cards</strong> - Increases
                            your chances of winning, but make sure you can keep
                            track of all of them
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="bg-purple-100 rounded-full p-1 text-purple-600 mt-0.5 mr-2 flex-shrink-0">
                            <Check size={14} />
                          </div>
                          <span>
                            <strong>Pay attention to called numbers</strong> -
                            Missing a number can cost you a win
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="bg-purple-100 rounded-full p-1 text-purple-600 mt-0.5 mr-2 flex-shrink-0">
                            <Check size={14} />
                          </div>
                          <span>
                            <strong>
                              Look for cards with good number distribution
                            </strong>{" "}
                            - Cards with numbers spread across the range may
                            perform better
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="bg-purple-100 rounded-full p-1 text-purple-600 mt-0.5 mr-2 flex-shrink-0">
                            <Check size={14} />
                          </div>
                          <span>
                            <strong>Stay alert near the end of the game</strong>{" "}
                            - As more numbers are called, players get closer to
                            winning
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-5 rounded-lg">
                      <h3 className="text-xl font-bold text-indigo-800 mb-3 flex items-center">
                        <Star className="text-indigo-500 mr-2" size={20} />
                        For Hosts
                      </h3>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <div className="bg-indigo-100 rounded-full p-1 text-indigo-600 mt-0.5 mr-2 flex-shrink-0">
                            <Check size={14} />
                          </div>
                          <span>
                            <strong>Call numbers clearly</strong> - Ensure all
                            players can hear the called numbers
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="bg-indigo-100 rounded-full p-1 text-indigo-600 mt-0.5 mr-2 flex-shrink-0">
                            <Check size={14} />
                          </div>
                          <span>
                            <strong>Adjust calling speed</strong> - Match the
                            pace to your audience's experience level
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="bg-indigo-100 rounded-full p-1 text-indigo-600 mt-0.5 mr-2 flex-shrink-0">
                            <Check size={14} />
                          </div>
                          <span>
                            <strong>Keep a record of called numbers</strong> -
                            Essential for verifying wins
                          </span>
                        </li>
                        <li className="flex items-start">
                          <div className="bg-indigo-100 rounded-full p-1 text-indigo-600 mt-0.5 mr-2 flex-shrink-0">
                            <Check size={14} />
                          </div>
                          <span>
                            <strong>Mix up patterns</strong> - Variety keeps the
                            game interesting for regular players
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-2xl font-semibold text-gray-700 mb-3">
                      Maximizing Your Experience
                    </h3>
                    <p className="mb-4">
                      Whether you're playing at a community hall, online, or at
                      home with friends, these tips will help you make the most
                      of your bingo experience:
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-purple-700">
                          For Social Players
                        </h4>
                        <p className="mt-2 text-sm">
                          Focus on the social aspect of bingo. Chat with other
                          players between games, participate in side games, and
                          enjoy the community atmosphere. The more you engage,
                          the more enjoyable the experience.
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-purple-700">
                          For Competitive Players
                        </h4>
                        <p className="mt-2 text-sm">
                          Pay close attention to patterns and called numbers.
                          Position multiple cards so you can scan them
                          efficiently. Consider using different colored daubers
                          for different patterns in complex games.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Get Started Section */}
              <section className="mb-8 max-w-4xl mx-auto">
                <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-xl p-8 text-white shadow-xl">
                  <h2 className="text-3xl font-bold mb-4">
                    Ready to Play Bingo?
                  </h2>
                  <p className="text-lg mb-6">
                    Now that you're familiar with the rules, variations, and
                    strategies, it's time to start playing! Whether you're
                    hosting a game for friends or joining an existing one, Call
                    My Bingo has everything you need.
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
                    <Link
                      to="/"
                      className="bg-white text-purple-600 hover:bg-purple-50 px-6 py-3 rounded-full font-bold transition-colors shadow-md"
                    >
                      Start Calling Numbers
                    </Link>
                    <button
                      onClick={() =>
                        (window.location.href = "/#generate-cards")
                      }
                      className="bg-purple-800 hover:bg-purple-900 text-white px-6 py-3 rounded-full font-bold transition-colors shadow-md"
                    >
                      Bingo Cards Generator
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HowToPlayPage;
